/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { FileText, Clock, Tag, Wrench, Loader, Trash2 } from 'lucide-react';
import { UserStory } from '../../types';
import { AcceptanceCriterion } from '@shared/types/user-stories';
import type { Issue } from '@shared/types/issues';
import { UserStoryDetails } from './UserStoryDetails';

interface UserStoryCardProps {
  story: UserStory;
  isExpanded: boolean;
  isGrooming: boolean;
  isEditing: boolean;
  isUpdating: boolean;
  editedFields: Partial<UserStory>;
  statusOptions: string[];
  releases?: Issue[];
  getStatusColor: (status: string) => string;
  onToggleExpansion: () => void;
  onGroomStory: () => void;
  onStartEditing: () => void;
  onCancelEditing: () => void;
  onSaveEdits: () => void;
  onFieldChange: (field: keyof UserStory, value: any) => void;
  onStatusUpdate: (newStatus: string) => void;
  onAcceptanceCriteriaChange: (criteria: AcceptanceCriterion[]) => void;
  onLinksChange: (links: string[]) => void;
  onRequestDelete: () => void;
}

export function UserStoryCard({
  story,
  isExpanded,
  isGrooming,
  isEditing,
  isUpdating,
  editedFields,
  statusOptions,
  releases = [],
  getStatusColor,
  onToggleExpansion,
  onGroomStory,
  onStartEditing,
  onCancelEditing,
  onSaveEdits,
  onFieldChange,
  onStatusUpdate,
  onAcceptanceCriteriaChange,
  onLinksChange,
  onRequestDelete
}: UserStoryCardProps) {
  return (
    <div
      id={story.id}
      style={{
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '16px',
        background: 'white',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s, background-color 0.5s'
      }}
      onClick={(e) => {
        // Don't expand if clicking on buttons
        if ((e.target as HTMLElement).closest('button')) {
          return;
        }
        onToggleExpansion();
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FileText size={16} style={{ color: '#0066cc' }} />
          <h4 style={{ margin: 0, color: '#333' }}>{story.id}: {story.title}</h4>
          {story.status && (
            <span
              style={{
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600',
                backgroundColor: getStatusColor(story.status),
                color: 'white'
              }}
            >
              {story.status}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            type="button"
            onClick={onRequestDelete}
            className="btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              fontSize: '12px',
              backgroundColor: 'transparent',
              border: '1px solid #dc3545',
              borderRadius: '4px',
              color: '#dc3545',
              justifyContent: 'center'
            }}
            title="Delete this user story from the repository"
          >
            <Trash2 size={12} />
          </button>        
          <button
            onClick={onGroomStory}
            disabled={isGrooming}
            className="btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              fontSize: '12px',
              backgroundColor: '#6f42c1',
              borderColor: '#6f42c1',
              minWidth: '80px',
              justifyContent: 'center'
            }}
            title="Break down this user story into implementable issues"
          >
            {isGrooming ? (
              <>
                <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} />
                Grooming
              </>
            ) : (
              <>
                <Wrench size={12} />
                Groom
              </>
            )}
          </button>
        </div>
      </div>

      <div style={{ margin: '0 0 12px 0', color: '#666' }}>
        <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
          <strong>As</strong> {story.role}, <strong>I want to</strong> {story.goal} <strong>so that</strong> {story.benefit}
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '14px', color: '#666' }}>
        {story.section && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Tag size={14} />
            {story.section}
          </div>
        )}
        
        {story.epicId && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Tag size={14} />
            Epic: {story.epicId}
          </div>
        )}
        
        {story.acceptanceCriteria && story.acceptanceCriteria.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Clock size={14} />
            {story.acceptanceCriteria.length} acceptance criteria
          </div>
        )}
      </div>

      {/* Expanded view */}
      {isExpanded && (
        <UserStoryDetails
          story={story}
          isEditing={isEditing}
          isUpdating={isUpdating}
          editedFields={editedFields}
          statusOptions={statusOptions}
          releases={releases}
          getStatusColor={getStatusColor}
          onStartEditing={onStartEditing}
          onCancelEditing={onCancelEditing}
          onSaveEdits={onSaveEdits}
          onFieldChange={onFieldChange}
          onStatusUpdate={onStatusUpdate}
          onAcceptanceCriteriaChange={onAcceptanceCriteriaChange}
          onLinksChange={onLinksChange}
        />
      )}
    </div>
  );
}
